import React, { useState, useContext, useRef, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { 
  Upload, 
  Clipboard, 
  Camera, 
  Loader2, 
  CheckCircle, 
  AlertTriangle, 
  AlertCircle,
  X,
  Edit,
  Save,
  Trash2,
  Home,
  MapPin,
  Bed,
  Bath,
  Ruler,
  Car,
  Trees,
  Calendar,
  PoundSterling,
  Building,
  Image as ImageIcon
} from 'lucide-react';
import { getErrorMessage } from '../utils/errorUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOURCE_TYPES = [
  { value: 'Rightmove', label: 'Rightmove' },
  { value: 'Zoopla', label: 'Zoopla' },
  { value: 'Agent', label: 'Agent Brochure' },
  { value: 'LR', label: 'Land Registry' },
  { value: 'Spreadsheet', label: 'Spreadsheet' },
  { value: 'Other', label: 'Other' }
];

const CONFIDENCE_COLORS = {
  high: 'bg-green-100 text-green-800 border-green-300',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  low: 'bg-red-100 text-red-800 border-red-300'
};

const CONFIDENCE_ICONS = {
  high: <CheckCircle className="h-3 w-3" />,
  medium: <AlertTriangle className="h-3 w-3" />,
  low: <AlertCircle className="h-3 w-3" />
};

export default function ScreenshotComparableDialog({ open, onClose, inspection, onSuccess }) {
  const { token } = useContext(AuthContext);
  const fileInputRef = useRef(null);
  
  // State
  const [step, setStep] = useState('upload'); // upload, processing, review
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [sourceType, setSourceType] = useState('Other');
  const [subjectPostcode, setSubjectPostcode] = useState('');
  const [valuationDate, setValuationDate] = useState(new Date().toISOString().split('T')[0]);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Extraction results
  const [parsedComparable, setParsedComparable] = useState(null);
  const [confidenceByField, setConfidenceByField] = useState({});
  const [warnings, setWarnings] = useState([]);
  const [screenshotFileId, setScreenshotFileId] = useState(null);
  
  // Editable fields
  const [editableData, setEditableData] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  // Handle file upload
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  // Handle paste from clipboard
  const handlePaste = useCallback(async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        for (const type of item.types) {
          if (type.startsWith('image/')) {
            const blob = await item.getType(type);
            const file = new File([blob], 'pasted-image.png', { type });
            processFile(file);
            return;
          }
        }
      }
      toast.error('No image found in clipboard');
    } catch (err) {
      toast.error('Unable to paste from clipboard. Try uploading instead.');
    }
  }, []);

  // Process file to base64
  const processFile = (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, WEBP)');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      // Extract base64 without data URL prefix
      const base64 = e.target.result.split(',')[1];
      setImageBase64(base64);
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Extract data from screenshot
  const extractData = async () => {
    if (!imageBase64) {
      toast.error('Please select or paste an image first');
      return;
    }

    setProcessing(true);
    setError(null);
    setStep('processing');

    try {
      const response = await axios.post(
        `${API}/comparables/extract-from-screenshot`,
        {
          inspection_id: inspection.id,
          screenshot_base64: imageBase64,
          source_type: sourceType,
          subject_postcode: subjectPostcode || null,
          valuation_date: valuationDate || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const parsed = response.data.parsed_comparable || {};
        setParsedComparable(parsed);
        setEditableData({ ...parsed });
        setScreenshotFileId(parsed.screenshot_file_id);
        
        // Convert confidence array to object for easy lookup
        const confMap = {};
        (response.data.confidence_by_field || []).forEach(item => {
          confMap[item.field_name] = item.confidence;
        });
        setConfidenceByField(confMap);
        setWarnings(response.data.warnings || []);
        setStep('review');
      } else {
        setError(response.data.error_message || 'Extraction failed');
        setStep('upload');
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to extract data from screenshot'));
      setStep('upload');
    } finally {
      setProcessing(false);
    }
  };

  // Save comparable to schedule
  const saveComparable = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/comparables/save-extracted?inspection_id=${inspection.id}&screenshot_file_id=${screenshotFileId}`,
        editableData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Comparable saved to schedule');
      onSuccess?.();
      handleReset();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save comparable'));
    } finally {
      setSaving(false);
    }
  };

  // Reset dialog state
  const handleReset = () => {
    setStep('upload');
    setImagePreview(null);
    setImageBase64(null);
    setSourceType('Other');
    setSubjectPostcode('');
    setError(null);
    setParsedComparable(null);
    setEditableData({});
    setConfidenceByField({});
    setWarnings([]);
    setIsEditing(false);
  };

  // Update editable field
  const updateField = (field, value) => {
    setEditableData(prev => ({ ...prev, [field]: value }));
  };

  // Get confidence badge for field
  const getConfidenceBadge = (field) => {
    const conf = confidenceByField[field] || 'low';
    return (
      <Badge 
        variant="outline" 
        className={`text-xs ${CONFIDENCE_COLORS[conf]}`}
      >
        {CONFIDENCE_ICONS[conf]}
        <span className="ml-1 capitalize">{conf}</span>
      </Badge>
    );
  };

  // Render upload step
  const renderUploadStep = () => (
    <div className="space-y-6">
      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors
          ${imagePreview ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}
      >
        {imagePreview ? (
          <div className="space-y-4">
            <img 
              src={imagePreview} 
              alt="Preview" 
              className="max-h-64 mx-auto rounded-lg shadow-md"
            />
            <div className="flex justify-center gap-2">
              <Button variant="outline" size="sm" onClick={() => { setImagePreview(null); setImageBase64(null); }}>
                <X className="h-4 w-4 mr-1" /> Remove
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="p-4 bg-gray-100 rounded-full">
                <ImageIcon className="h-12 w-12 text-gray-400" />
              </div>
            </div>
            <div>
              <p className="text-lg font-medium text-gray-700">Drop screenshot here</p>
              <p className="text-sm text-gray-500">or use the buttons below</p>
            </div>
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                <Upload className="h-4 w-4 mr-2" /> Upload Image
              </Button>
              <Button variant="outline" onClick={handlePaste}>
                <Clipboard className="h-4 w-4 mr-2" /> Paste from Clipboard
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        )}
      </div>

      {/* Options */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>Source Type (Optional)</Label>
          <Select value={sourceType} onValueChange={setSourceType}>
            <SelectTrigger data-testid="source-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SOURCE_TYPES.map(src => (
                <SelectItem key={src.value} value={src.value}>{src.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Subject Postcode (Optional)</Label>
          <Input
            placeholder="e.g., SW1A 1AA"
            value={subjectPostcode}
            onChange={(e) => setSubjectPostcode(e.target.value.toUpperCase())}
            data-testid="subject-postcode-input"
          />
          <p className="text-xs text-muted-foreground mt-1">For distance calculation</p>
        </div>
        <div>
          <Label>Valuation Date</Label>
          <Input
            type="date"
            value={valuationDate}
            onChange={(e) => setValuationDate(e.target.value)}
            data-testid="valuation-date-input"
          />
        </div>
      </div>

      {/* Error display */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-800">Extraction Error</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Render processing step
  const renderProcessingStep = () => (
    <div className="py-12 text-center space-y-4">
      <Loader2 className="h-12 w-12 animate-spin mx-auto text-blue-500" />
      <div>
        <p className="text-lg font-medium">Analyzing Screenshot...</p>
        <p className="text-sm text-muted-foreground">Extracting property details using AI vision</p>
      </div>
      <div className="flex justify-center">
        <img 
          src={imagePreview} 
          alt="Processing" 
          className="max-h-32 rounded-lg opacity-50"
        />
      </div>
    </div>
  );

  // Render review step
  const renderReviewStep = () => (
    <div className="space-y-6">
      {/* Warnings */}
      {warnings.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800 text-sm">Extraction Warnings</p>
                <ul className="text-sm text-amber-700 mt-1 space-y-1">
                  {warnings.map((w, i) => <li key={i}>• {w}</li>)}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overall confidence */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Extraction Confidence:</span>
          <Badge variant={editableData.extraction_confidence > 0.7 ? 'default' : editableData.extraction_confidence > 0.4 ? 'secondary' : 'destructive'}>
            {Math.round((editableData.extraction_confidence || 0) * 100)}%
          </Badge>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsEditing(!isEditing)}
        >
          <Edit className="h-4 w-4 mr-1" />
          {isEditing ? 'View Mode' : 'Edit Mode'}
        </Button>
      </div>

      {/* Extracted data card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Building className="h-5 w-5 text-blue-500" />
            Extracted Comparable Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Address Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> Address
                </Label>
                {getConfidenceBadge('address')}
              </div>
              {isEditing ? (
                <Input
                  value={editableData.address || ''}
                  onChange={(e) => updateField('address', e.target.value)}
                  className={!editableData.address ? 'border-amber-300 bg-amber-50' : ''}
                />
              ) : (
                <p className={`text-sm p-2 bg-muted/50 rounded ${!editableData.address ? 'text-amber-600 italic' : ''}`}>
                  {editableData.address || 'Not detected'}
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Postcode</Label>
                {getConfidenceBadge('postcode')}
              </div>
              {isEditing ? (
                <Input
                  value={editableData.postcode || ''}
                  onChange={(e) => updateField('postcode', e.target.value.toUpperCase())}
                  className={!editableData.postcode ? 'border-amber-300 bg-amber-50' : ''}
                />
              ) : (
                <p className={`text-sm p-2 bg-muted/50 rounded ${!editableData.postcode ? 'text-amber-600 italic' : ''}`}>
                  {editableData.postcode || 'Not detected'}
                </p>
              )}
            </div>
          </div>

          <Separator />

          {/* Property Details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs flex items-center gap-1">
                  <Home className="h-3 w-3" /> Type
                </Label>
                {getConfidenceBadge('property_type')}
              </div>
              {isEditing ? (
                <Select value={editableData.property_type || ''} onValueChange={(v) => updateField('property_type', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">Flat</SelectItem>
                    <SelectItem value="house">House</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="land">Land</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm p-2 bg-muted/50 rounded capitalize">
                  {editableData.property_type || '-'}
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Tenure</Label>
                {getConfidenceBadge('tenure')}
              </div>
              {isEditing ? (
                <Select value={editableData.tenure || ''} onValueChange={(v) => updateField('tenure', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="freehold">Freehold</SelectItem>
                    <SelectItem value="leasehold">Leasehold</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm p-2 bg-muted/50 rounded capitalize">
                  {editableData.tenure || '-'}
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs flex items-center gap-1">
                  <Bed className="h-3 w-3" /> Beds
                </Label>
                {getConfidenceBadge('bedrooms')}
              </div>
              {isEditing ? (
                <Input
                  type="number"
                  min="0"
                  max="20"
                  value={editableData.bedrooms ?? ''}
                  onChange={(e) => updateField('bedrooms', e.target.value ? parseInt(e.target.value) : null)}
                  className="h-9"
                />
              ) : (
                <p className="text-sm p-2 bg-muted/50 rounded">
                  {editableData.bedrooms ?? '-'}
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs flex items-center gap-1">
                  <Bath className="h-3 w-3" /> Baths
                </Label>
                {getConfidenceBadge('bathrooms')}
              </div>
              {isEditing ? (
                <Input
                  type="number"
                  min="0"
                  max="20"
                  value={editableData.bathrooms ?? ''}
                  onChange={(e) => updateField('bathrooms', e.target.value ? parseInt(e.target.value) : null)}
                  className="h-9"
                />
              ) : (
                <p className="text-sm p-2 bg-muted/50 rounded">
                  {editableData.bathrooms ?? '-'}
                </p>
              )}
            </div>
          </div>

          <Separator />

          {/* Price and Area */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs flex items-center gap-1">
                  <PoundSterling className="h-3 w-3" /> Price (£)
                </Label>
                {getConfidenceBadge('price')}
              </div>
              {isEditing ? (
                <Input
                  type="number"
                  value={editableData.price ?? ''}
                  onChange={(e) => updateField('price', e.target.value ? parseFloat(e.target.value) : null)}
                  className={`h-9 ${!editableData.price ? 'border-red-300 bg-red-50' : ''}`}
                />
              ) : (
                <p className={`text-sm p-2 bg-muted/50 rounded font-medium ${!editableData.price ? 'text-red-600' : ''}`}>
                  {editableData.price ? `£${editableData.price.toLocaleString()}` : 'Not detected'}
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Price Type</Label>
                {getConfidenceBadge('price_type')}
              </div>
              {isEditing ? (
                <Select value={editableData.price_type || 'sale'} onValueChange={(v) => updateField('price_type', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">Sale</SelectItem>
                    <SelectItem value="rent">Rent</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm p-2 bg-muted/50 rounded capitalize">
                  {editableData.price_type || 'sale'}
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs flex items-center gap-1">
                  <Ruler className="h-3 w-3" /> Area (m²)
                </Label>
                {getConfidenceBadge('floor_area_sqm')}
              </div>
              {isEditing ? (
                <Input
                  type="number"
                  step="0.1"
                  value={editableData.floor_area_sqm ?? ''}
                  onChange={(e) => updateField('floor_area_sqm', e.target.value ? parseFloat(e.target.value) : null)}
                  className="h-9"
                />
              ) : (
                <p className="text-sm p-2 bg-muted/50 rounded">
                  {editableData.floor_area_sqm ? `${editableData.floor_area_sqm} m²` : '-'}
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Status</Label>
                {getConfidenceBadge('asking_or_achieved')}
              </div>
              {isEditing ? (
                <Select value={editableData.asking_or_achieved || 'unknown'} onValueChange={(v) => updateField('asking_or_achieved', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asking">Asking</SelectItem>
                    <SelectItem value="achieved">Achieved</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm p-2 bg-muted/50 rounded capitalize">
                  {editableData.asking_or_achieved || '-'}
                </p>
              )}
            </div>
          </div>

          <Separator />

          {/* Additional Details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Date
                </Label>
                {getConfidenceBadge('date_of_sale_or_listed')}
              </div>
              {isEditing ? (
                <Input
                  type="date"
                  value={editableData.date_of_sale_or_listed || ''}
                  onChange={(e) => updateField('date_of_sale_or_listed', e.target.value)}
                  className="h-9"
                />
              ) : (
                <p className="text-sm p-2 bg-muted/50 rounded">
                  {editableData.date_of_sale_or_listed || '-'}
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs flex items-center gap-1">
                  <Car className="h-3 w-3" /> Parking
                </Label>
                {getConfidenceBadge('parking')}
              </div>
              {isEditing ? (
                <Select value={editableData.parking || 'unknown'} onValueChange={(v) => updateField('parking', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm p-2 bg-muted/50 rounded capitalize">
                  {editableData.parking || '-'}
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs flex items-center gap-1">
                  <Trees className="h-3 w-3" /> Outside
                </Label>
                {getConfidenceBadge('outside_space')}
              </div>
              {isEditing ? (
                <Select value={editableData.outside_space || 'unknown'} onValueChange={(v) => updateField('outside_space', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm p-2 bg-muted/50 rounded capitalize">
                  {editableData.outside_space || '-'}
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Distance (km)</Label>
              </div>
              <p className="text-sm p-2 bg-muted/50 rounded">
                {editableData.distance_to_subject_km ? `${editableData.distance_to_subject_km} km` : '-'}
              </p>
            </div>
          </div>

          {/* Condition Note */}
          {(editableData.condition_note || isEditing) && (
            <>
              <Separator />
              <div>
                <Label className="text-xs">Condition Note</Label>
                {isEditing ? (
                  <Input
                    value={editableData.condition_note || ''}
                    onChange={(e) => updateField('condition_note', e.target.value)}
                    placeholder="e.g., Recently refurbished, needs modernisation"
                  />
                ) : (
                  <p className="text-sm p-2 bg-muted/50 rounded">
                    {editableData.condition_note || '-'}
                  </p>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Source info */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>Source: {editableData.evidence_source || sourceType}</span>
        <span>Screenshot ID: {screenshotFileId?.slice(0, 8)}...</span>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleReset(); onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[95vh] flex flex-col overflow-hidden" data-testid="screenshot-comparable-dialog">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-blue-500" />
            Add Comparable from Screenshot
          </DialogTitle>
          <DialogDescription>
            Upload or paste a screenshot from Rightmove, Zoopla, agent brochure, or Land Registry
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-1 py-4">
          {step === 'upload' && renderUploadStep()}
          {step === 'processing' && renderProcessingStep()}
          {step === 'review' && renderReviewStep()}
        </div>

        <DialogFooter className="flex-shrink-0 pt-4 border-t">
          {step === 'upload' && (
            <>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={extractData} 
                disabled={!imageBase64 || processing}
                data-testid="extract-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Camera className="h-4 w-4 mr-2" />
                    Extract Data
                  </>
                )}
              </Button>
            </>
          )}
          {step === 'review' && (
            <>
              <Button variant="outline" onClick={handleReset}>
                <Trash2 className="h-4 w-4 mr-2" />
                Discard
              </Button>
              <Button variant="outline" onClick={() => setStep('upload')}>
                <ImageIcon className="h-4 w-4 mr-2" />
                New Screenshot
              </Button>
              <Button 
                onClick={saveComparable} 
                disabled={saving}
                data-testid="save-comparable-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save to Schedule
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
